import { addSoundSets, bass as triBass, kick, noise, tone } from "./sounds";

// Sound sets for the holiday events through the year (see lib/season.js),
// made like the Halloween set in lib/sounds.js: during an event, its set
// replaces the arcade sounds with the same names. Every set is built from a
// kit (a melodic voice, a scale, a drum and a click), and then swaps in its
// own special sounds.

// Note names to pitches: hz("A4") is 440. A "q" lowers a note by a quarter
// tone, for the Arabic scales ("Eq4" is E half-flat).
const STEPS = { C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 };
export function hz(name) {
  const [, letter, acc, octave] = name.match(/^([A-G])([#bq]?)(\d)$/);
  const shift = { "#": 1, b: -1, q: -0.5 }[acc] || 0;
  return 440 * 2 ** ((STEPS[letter] + shift + (octave - 4) * 12) / 12);
}
const semis = (f, n) => f * 2 ** (n / 12);

// ---------- Instruments ----------

// A struck bell, with a bell's slightly out-of-tune overtones.
const bell = (at, f, vol = 0.1, len = 1.4) =>
  [
    [1, 1],
    [2, 0.5],
    [2.76, 0.32],
    [5.4, 0.14],
  ].forEach(([m, v], i) => tone(at, f * m, { type: "sine", vol: vol * v, env: { d: len / (1 + i * 0.7), s: 0.0001, r: 0.1 } }));
// A glockenspiel or music box note.
const glock = (at, f, vol = 0.1, len = 0.6) => {
  tone(at, f, { type: "sine", vol, env: { d: len, s: 0.0001, r: 0.05 } });
  tone(at, f * 4, { type: "sine", vol: vol * 0.18, env: { d: len * 0.25, s: 0.0001, r: 0.03 } });
};
// Sleigh bells: a quick shake of tiny bells.
const jingle = (at, vol = 0.07, shakes = 4) => {
  for (let i = 0; i < shakes; i++) {
    const t = at + i * 0.06;
    noise(t, { filter: "bandpass", freq: 7500, q: 3, vol, len: 0.06 });
    tone(t, 5200 + (i % 3) * 650, { type: "sine", vol: vol * 0.25, env: { d: 0.09, s: 0.0001, r: 0.02 } });
    tone(t + 0.01, 6900 - (i % 2) * 500, { type: "sine", vol: vol * 0.2, env: { d: 0.07, s: 0.0001, r: 0.02 } });
  }
};
const harp = (at, f, vol = 0.13, len = 1) =>
  tone(at, f, { type: "triangle", vol, filter: { freq: 4500, to: 900 }, env: { a: 0.002, d: len, s: 0.0001, r: 0.1 } });
const strings = (at, f, len, vol = 0.018) => {
  for (const detune of [-7, 7]) {
    tone(at, f, { type: "sawtooth", vol, detune, vib: [5, f * 0.006], filter: { freq: 2200 }, env: { a: 0.15, d: 0.1, s: 0.8, hold: len, r: 0.4 } });
  }
};
const brass = (at, f, len, vol = 0.035) => {
  for (const detune of [-6, 6]) {
    tone(at, f, { type: "sawtooth", vol, detune, filter: { freq: 700, to: 2600 }, env: { a: 0.03, d: 0.08, s: 0.75, hold: len, r: 0.1 } });
  }
};
// A samba whistle's trill.
const whistle = (at, len = 0.3, vol = 0.045) =>
  tone(at, 2600, { type: "sine", vol, vib: [28, 160], env: { a: 0.01, d: 0.02, s: 0.9, hold: len, r: 0.03 } });
const cowbell = (at, f = 800, vol = 0.04) => {
  tone(at, f, { type: "square", vol, filter: { type: "bandpass", freq: f * 1.5 }, env: { d: 0.14, s: 0.0001, r: 0.02 } });
  tone(at, f * 1.48, { type: "square", vol: vol * 0.7, filter: { type: "bandpass", freq: f * 2 }, env: { d: 0.1, s: 0.0001, r: 0.02 } });
};
const kazoo = (at, f, len, vol = 0.05) =>
  tone(at, f, { type: "sawtooth", vol, vib: [7, f * 0.02], filter: { type: "bandpass", freq: 1400 }, env: { a: 0.02, d: 0.05, s: 0.8, hold: len, r: 0.05 } });
// Oud and qanun: plucked strings with a quick bright attack.
const oud = (at, f, vol = 0.05, len = 0.5) =>
  tone(at, f * 1.012, { type: "sawtooth", vol, to: f, slide: 0.03, filter: { freq: 3200, to: 500 }, env: { a: 0.002, d: len, s: 0.0001, r: 0.05 } });
const qanun = (at, f, vol = 0.04) => {
  oud(at, f * 2, vol, 0.3);
  oud(at + 0.05, f * 2, vol * 0.65, 0.3);
};
// Darbuka: the deep "doum" and the sharp "tek".
const doum = (at, vol = 0.3) => {
  tone(at, 115, { type: "sine", vol, to: 62, slide: 0.1, env: { d: 0.25, s: 0.0001, r: 0.05 } });
  noise(at, { filter: "lowpass", freq: 500, vol: vol * 0.2, len: 0.05 });
};
const tek = (at, vol = 0.1) => {
  noise(at, { filter: "bandpass", freq: 3200, q: 2, vol, len: 0.03 });
  tone(at, 720, { type: "sine", vol: vol * 0.5, env: { d: 0.03, s: 0.0001, r: 0.01 } });
};
// The ney, a breathy reed flute.
const ney = (at, f, len, vol = 0.11) => {
  tone(at, f, { type: "sine", vol, vib: [5, f * 0.01], env: { a: 0.08, d: 0.1, s: 0.8, hold: len, r: 0.2 } });
  noise(at, { filter: "bandpass", freq: f * 2, q: 3, vol: vol * 0.25, env: { a: 0.08, d: 0.1, s: 0.6, hold: len, r: 0.2 } });
};
// A tin whistle, with the little grace note Irish tunes put before a note.
const tinWhistle = (at, f, len = 0.12, vol = 0.09) => {
  tone(at, f * 1.12, { type: "sine", vol: vol * 0.6, env: { a: 0.003, d: 0.02, s: 0.0001, r: 0.01 } });
  tone(at + 0.022, f, { type: "sine", vol, vib: [6, f * 0.007], env: { a: 0.01, d: 0.03, s: 0.85, hold: len, r: 0.05 } });
  noise(at + 0.022, { filter: "bandpass", freq: f * 2, q: 4, vol: vol * 0.2, env: { a: 0.01, d: 0.03, s: 0.6, hold: len, r: 0.05 } });
};
const bodhran = (at, vol = 0.3) => {
  tone(at, 95, { type: "sine", vol, to: 68, slide: 0.08, env: { d: 0.18, s: 0.0001, r: 0.04 } });
  noise(at, { filter: "lowpass", freq: 900, vol: vol * 0.25, len: 0.06 });
};
const birdChirp = (at, f = 3600, vol = 0.05) => {
  tone(at, f, { type: "sine", vol, to: f * 1.45, slide: 0.05, env: { d: 0.06, s: 0.0001, r: 0.01 } });
  tone(at + 0.08, f * 1.25, { type: "sine", vol, to: f * 0.9, slide: 0.05, env: { d: 0.05, s: 0.0001, r: 0.01 } });
};
const marimba = (at, f, vol = 0.17, len = 0.4) => {
  tone(at, f, { type: "sine", vol, env: { d: len, s: 0.0001, r: 0.05 } });
  tone(at, f * 4, { type: "sine", vol: vol * 0.14, env: { d: 0.04, s: 0.0001, r: 0.01 } });
};
const steelpan = (at, f, vol = 0.11, len = 0.7) => {
  tone(at, f, { type: "sine", vol, to: f * 0.995, env: { d: len, s: 0.0001, r: 0.05 } });
  tone(at, f * 2, { type: "sine", vol: vol * 0.5, env: { d: len * 0.6, s: 0.0001, r: 0.05 } });
  tone(at, f * 3.01, { type: "sine", vol: vol * 0.2, env: { d: len * 0.3, s: 0.0001, r: 0.05 } });
};
const uke = (at, f, vol = 0.1, len = 0.45) =>
  tone(at, f, { type: "triangle", vol, filter: { freq: 5000, to: 1500 }, env: { a: 0.002, d: len, s: 0.0001, r: 0.05 } });
const shaker = (at, vol = 0.05) => noise(at, { filter: "highpass", freq: 6000, vol, env: { a: 0.02, d: 0.06, s: 0.0001, r: 0.02 } });
const waves = (at, vol = 0.12, len = 1.6) =>
  noise(at, { filter: "lowpass", freq: 400, to: 1400, vol, env: { a: len * 0.5, d: 0.1, s: 0.6, r: len * 0.5 } });
const gull = (at, vol = 0.05) =>
  [0, 0.22, 0.42].forEach((d, i) =>
    tone(at + d, 1500 - i * 90, { type: "sawtooth", vol, to: 900, vib: [30, 40], filter: { type: "bandpass", freq: 1800 }, env: { a: 0.01, d: 0.05, s: 0.7, hold: 0.1, r: 0.05 } }),
  );
// Fireworks: a whistling rocket, the bang and the crackle.
const rocket = (at, vol = 0.05) => {
  tone(at, 700, { type: "sine", vol, to: 2600, slide: 0.5, env: { a: 0.05, d: 0.05, s: 0.8, hold: 0.4, r: 0.03 } });
  noise(at, { filter: "highpass", freq: 4000, vol: vol * 0.6, env: { a: 0.05, d: 0.05, s: 0.6, hold: 0.4, r: 0.03 } });
};
const burst = (at, vol = 0.3) => {
  kick(at, vol);
  noise(at, { filter: "lowpass", freq: 2500, to: 150, vol, len: 0.6 });
  for (let i = 0; i < 16; i++) noise(at + 0.12 + ((i * 37) % 100) / 110, { filter: "highpass", freq: 5000, vol: 0.07, len: 0.008 });
};
const cork = (at, vol = 0.2) => {
  tone(at, 520, { type: "sine", vol, to: 150, slide: 0.04, env: { d: 0.05, s: 0.0001, r: 0.02 } });
  noise(at, { filter: "bandpass", freq: 1500, q: 2, vol: vol * 0.6, len: 0.02 });
};
const fizz = (at, len = 1, vol = 0.05) => {
  for (let i = 0; i < len * 30; i++) noise(at + ((i * 53) % 97) / 97 * len, { filter: "highpass", freq: 7000, vol: vol * (1 - i / (len * 40)), len: 0.004 });
};
const partyHorn = (at, vol = 0.05) =>
  tone(at, 330, { type: "sawtooth", vol, to: 370, vib: [22, 6], filter: { type: "bandpass", freq: 1200 }, env: { a: 0.02, d: 0.05, s: 0.8, hold: 0.35, r: 0.05 } });
const tick = (at, f = 2000, vol = 0.08) => {
  tone(at, f, { type: "sine", vol, env: { d: 0.02, s: 0.0001, r: 0.01 } });
  noise(at, { filter: "bandpass", freq: f * 2, q: 5, vol: vol * 0.5, len: 0.01 });
};
// A kiss: a little smack and a rising "mwah".
const kiss = (at, vol = 0.1) => {
  noise(at, { filter: "bandpass", freq: 2200, q: 3, vol: vol * 0.8, len: 0.015 });
  tone(at + 0.01, 400, { type: "sine", vol, to: 900, slide: 0.08, env: { d: 0.08, s: 0.0001, r: 0.02 } });
};
const heartbeat = (at, vol = 0.4) => {
  kick(at, vol);
  kick(at + 0.17, vol * 0.7);
};
const whoopee = (at, vol = 0.06) =>
  tone(at, 120, { type: "sawtooth", vol, to: 75, vib: [26, 25], filter: { freq: 900 }, env: { a: 0.02, d: 0.05, s: 0.85, hold: 0.55, r: 0.1 } });
const slideWhistle = (at, from, to, len, vol = 0.12) =>
  tone(at, from, { type: "sine", vol, to, slide: len, vib: [6, 10], env: { a: 0.02, d: 0.02, s: 0.9, hold: len, r: 0.05 } });
const clownHorn = (at, vol = 0.05) => {
  for (const f of [420, 445]) {
    tone(at, f, { type: "sawtooth", vol, to: f * 0.92, filter: { type: "bandpass", freq: 1300 }, env: { a: 0.01, d: 0.04, s: 0.8, hold: 0.12, r: 0.05 } });
  }
};
const bonk = (at, vol = 0.3) => {
  tone(at, 900, { type: "sine", vol: vol * 0.5, to: 300, slide: 0.06, env: { d: 0.1, s: 0.0001, r: 0.03 } });
  kick(at, vol);
  noise(at, { filter: "bandpass", freq: 1800, q: 4, vol: vol * 0.3, len: 0.03 });
};
const crickets = (at, len = 1.2, vol = 0.03) => {
  for (let t = 0; t < len; t += 0.32) {
    for (let i = 0; i < 4; i++) tone(at + t + i * 0.035, 4400, { type: "sine", vol, env: { d: 0.02, s: 0.0001, r: 0.01 } });
  }
};

// A soft choir "ooh": two voices a little apart, with a gentle vibrato.
const choir = (at, f, len, vol = 0.05) => {
  for (const detune of [-9, 9]) {
    tone(at, f, { type: "triangle", vol, detune, vib: [5, f * 0.008], filter: { type: "bandpass", freq: 900 }, env: { a: 0.2, d: 0.1, s: 0.8, hold: len, r: 0.5 } });
  }
};
// A sinking bubble, for watery sad moments.
const sink = (at, vol = 0.12) => tone(at, 700, { type: "sine", vol, to: 180, slide: 0.35, env: { d: 0.4, s: 0.0001, r: 0.05 } });
const crack = (at, vol = 0.12) => {
  noise(at, { filter: "bandpass", freq: 2500, q: 3, vol, len: 0.02 });
  noise(at + 0.03, { filter: "bandpass", freq: 1800, q: 3, vol: vol * 0.7, len: 0.02 });
};

// ---------- Building a set from a kit ----------

// kit: scale (note names, one octave), lead(at, f, len), click(at, f),
// optional chord(at, freqs, len), bass(at, f, len), hit(at, vol), pad(at,
// freqs, len) (a soft held layer under the big chords), garnish(at) (the
// event's own sparkle on the good moments, like sleigh bells or birdsong),
// and `sounds`: the set's own special sounds, which win over the ones made
// here.
function makeSet(kit) {
  const scale = kit.scale.map(hz);
  // Scale steps past the octave go up an octave (step 7 is the octave).
  const note = (i) => scale[((i % 7) + 7) % 7] * 2 ** Math.floor(i / 7);
  const { lead, click } = kit;
  const garnish = kit.garnish || (() => {});
  const plainChord = kit.chord || ((at, freqs, len) => freqs.forEach((f) => lead(at, f, len)));
  const chord = (at, freqs, len) => {
    plainChord(at, freqs, len);
    kit.pad?.(at, freqs, len);
  };
  const bass = kit.bass || ((at, f, len) => triBass(at, f, len, 0.22));
  const hit = kit.hit || ((at, vol = 0.35) => kick(at, vol));
  const triad = (i) => [note(i), note(i + 2), note(i + 4)];
  const notes = (at, steps, gap, len = 0.08, play = lead) => steps.forEach((s, i) => play(at + i * gap, note(s), len));

  return {
    pop: () => click(0, note(11)),
    bloop: () => notes(0, [9, 11], 0.05, 0, click),
    boing: () => notes(0, [4, 7], 0.08, 0.15),
    squeak: () => notes(0, [14, 16], 0.035, 0, click),
    honk: () => notes(0, [-3, -3], 0.16, 0.1),
    hmm: () => notes(0, [3, 2], 0.2, 0.25),
    slideUp: () => notes(0, [7, 9, 11, 14], 0.05, 0.05),
    slideDown: () => notes(0, [14, 11, 9, 7], 0.06, 0.05),
    start: () => {
      notes(0, [0, 2, 4], 0.09, 0.08);
      chord(0.3, [...triad(7), note(14)], 0.6);
      bass(0.3, note(0) / 2, 0.4);
      hit(0.3);
      garnish(0.3);
    },
    yourTurn: () => {
      notes(0, [7, 9, 11, 14, 16], 0.06, 0.06);
      garnish(0.3);
    },
    nextTurn: () => notes(0, [11, 7], 0.07, 0.06),
    question: () => notes(0, [9, 12], 0.12, 0.1),
    answer: () => click(0, note(14)),
    correct: () => {
      notes(0, [7, 9, 11], 0.1, 0.1);
      click(0.2, note(18));
      garnish(0.22);
    },
    solvedMe: () => {
      notes(0, [7, 9, 11, 14], 0.07, 0.07);
      chord(0.3, [...triad(7), note(14)], 0.6);
      bass(0.3, note(0) / 2, 0.5);
      hit(0.3);
      notes(0.3, [14, 16, 18, 21], 0.06, 0, click);
      garnish(0.55);
    },
    // A sad slide down, one semitone at a time.
    wrong: () => {
      [0, 1, 2, 4].forEach((n, i) => lead(i * 0.28, semis(note(4), -n), i === 3 ? 0.6 : 0.2));
      bass(0, note(0) / 2, 1.2);
    },
    skip: () => notes(0, [11, 4], 0.09, 0.1),
    reveal: () => {
      noise(0, { filter: "bandpass", freq: 200, to: 5000, q: 1.2, vol: 0.1, env: { a: 0.55, d: 0.03, s: 0.0001, r: 0.03 } });
      chord(0.6, [...triad(7), note(14)], 0.8);
      bass(0.6, note(0) / 2, 0.6);
      hit(0.6);
      garnish(0.65);
    },
    coin: () => notes(0, [14, 18], 0.07, 0, click),
    siren: () => notes(0, [11, 9, 11, 9], 0.16, 0.12),
    sparkle: () => {
      notes(0, [14, 15, 16, 18, 21], 0.04, 0, click);
      garnish(0.1);
    },
    // Three heavy hits, each a semitone lower.
    dun: () =>
      [
        [0, 0, 0.16],
        [-1, 0.28, 0.16],
        [-3, 0.56, 0.8],
      ].forEach(([n, at, len]) => {
        const f = semis(note(4), n) / 2;
        plainChord(at, [f, f * 1.5, f * 2], len);
        bass(at, f / 2, len);
        hit(at, 0.4);
      }),
    boom: () => {
      hit(0, 0.45);
      noise(0, { filter: "lowpass", freq: 900, to: 80, vol: 0.3, len: 0.6 });
    },
    // A fanfare: four chords climbing home.
    podium: () => {
      [0, 3, 4, 7].forEach((step, i) => {
        const at = i * 0.32;
        chord(at, triad(step + 7), i === 3 ? 1 : 0.25);
        bass(at, note(step) / 2, i === 3 ? 0.8 : 0.25);
        hit(at, i === 3 ? 0.4 : 0.25);
      });
      notes(0.96, [14, 16, 18, 21], 0.08, 0, click);
      garnish(1);
    },
    ...kit.sounds,
  };
}

// ---------- The sets ----------

// Melodies (all traditional, long out of copyright) as [note, beat] pairs.
const melody = (at, tune, beat, play) => {
  let t = at;
  for (const [n, beats] of tune) {
    if (n) play(t, hz(n), beat * beats * 0.8);
    t += beat * beats;
  }
};

// Christmas and winter: sleigh bells, church bells and a glockenspiel.
const christmas = makeSet({
  scale: ["C5", "D5", "E5", "F5", "G5", "A5", "B5"],
  lead: (at, f) => glock(at, f, 0.11),
  click: (at, f) => glock(at, f, 0.08, 0.3),
  chord: (at, freqs, len) => freqs.forEach((f, i) => (i ? glock(at, f, 0.06, len + 0.4) : bell(at, f / 2, 0.08, len + 0.8))),
  hit: (at, vol = 0.35) => jingle(at, vol * 0.2, 3),
  garnish: (at) => jingle(at, 0.04, 3),
  pad: (at, freqs, len) => choir(at, freqs[0] / 2, len, 0.045),
  sounds: {
    // Bells falling sadly down the scale.
    wrong: () => {
      ["G5", "F#5", "F5", "D5"].forEach((n, i) => glock(i * 0.28, hz(n), 0.1, i === 3 ? 1.2 : 0.4));
      bell(0.84, hz("G3"), 0.07, 1.4);
    },
    // Sleigh bells ringing high and low.
    siren: () => ["E6", "C6", "E6", "C6"].forEach((n, i) => {
      glock(i * 0.18, hz(n), 0.09, 0.3);
      jingle(i * 0.18, 0.04, 2);
    }),
    pop: () => jingle(0, 0.1, 1),
    squeak: () => jingle(0, 0.05, 2),
    // "Jingle Bells".
    start: () => {
      melody(0, [["E5", 1], ["E5", 1], ["E5", 2], ["E5", 1], ["E5", 1], ["E5", 2], ["E5", 1], ["G5", 1], ["C5", 1.5], ["D5", 0.5], ["E5", 3]], 0.13, (t, f) => glock(t, f, 0.11));
      jingle(0, 0.05, 8);
      jingle(1.05, 0.06, 6);
      triBass(1.05, hz("C3"), 0.6, 0.2);
    },
    roll: () => jingle(0, 0.08, 9),
    coin: () => {
      bell(0, hz("G5"), 0.07, 0.6);
      bell(0.08, hz("C6"), 0.08, 1.2);
    },
    // Winter wind and sleigh bells passing far away.
    night: () => {
      noise(0, { filter: "bandpass", freq: 500, to: 1100, q: 0.8, vol: 0.1, env: { a: 0.5, d: 0.6, s: 0.3, r: 0.7 } });
      for (let i = 0; i < 4; i++) jingle(0.5 + i * 0.32, 0.02 + i * 0.006, 3);
      bell(1.8, hz("G4"), 0.08, 1.4);
    },
    boom: () => {
      bell(0, hz("C3"), 0.22, 2.2);
      bell(0, hz("G3"), 0.1, 1.6);
      jingle(0, 0.08, 5);
    },
    // "We Wish You a Merry Christmas".
    podium: () => {
      melody(0, [["D5", 1], ["G5", 1], ["G5", 0.5], ["A5", 0.5], ["G5", 0.5], ["F#5", 0.5], ["E5", 1], ["C5", 1], ["E5", 1], ["A5", 1], ["A5", 0.5], ["B5", 0.5], ["A5", 0.5], ["G5", 0.5], ["F#5", 1], ["D5", 1], ["G5", 3]], 0.14, (t, f) => glock(t, f, 0.11));
      [[0.14, "G3"], [0.84, "C4"], [1.54, "D4"], [2.38, "G3"]].forEach(([t, n]) => bell(t, hz(n), 0.08, 0.9));
      jingle(2.38, 0.07, 8);
    },
  },
});

// New Year: champagne, a countdown, fireworks and party horns.
const newyear = makeSet({
  scale: ["F5", "G5", "A5", "Bb5", "C6", "D6", "E6"],
  lead: (at, f) => glock(at, f, 0.1, 0.5),
  click: (at, f) => tick(at, f, 0.06),
  hit: (at, vol = 0.35) => {
    kick(at, vol);
    noise(at, { freq: 6000, vol: 0.04, len: 0.3 });
  },
  garnish: (at) => fizz(at, 0.4, 0.04),
  pad: (at, freqs, len) => strings(at, freqs[0] / 2, len, 0.012),
  sounds: {
    // A party horn running out of breath.
    wrong: () => tone(0, 370, { type: "sawtooth", vol: 0.06, to: 140, slide: 0.9, vib: [14, 10], filter: { type: "bandpass", freq: 1100 }, env: { a: 0.02, d: 0.05, s: 0.85, hold: 0.85, r: 0.1 } }),
    siren: () => {
      partyHorn(0, 0.045);
      partyHorn(0.45, 0.045);
    },
    pop: () => cork(0, 0.16),
    bloop: () => {
      cork(0, 0.12);
      fizz(0.05, 0.3, 0.03);
    },
    honk: () => partyHorn(0),
    // Three, two, one, then a rocket bursts.
    start: () => {
      [0, 0.3, 0.6].forEach((t, i) => tick(t, i === 2 ? 2600 : 2000, 0.1));
      rocket(0.9, 0.05);
      burst(1.4, 0.3);
    },
    reveal: () => {
      rocket(0, 0.05);
      burst(0.5, 0.3);
      [hz("F5"), hz("A5"), hz("C6"), hz("F6")].forEach((f) => glock(0.5, f, 0.07, 1));
    },
    coin: () => {
      cork(0, 0.14);
      fizz(0.05, 0.8, 0.04);
    },
    sparkle: () => fizz(0, 0.6, 0.06),
    boom: () => burst(0, 0.4),
    roll: () => {
      for (let i = 0; i < 8; i++) tick(i * 0.06, 1500 + ((i * 397) % 900), 0.07);
    },
    // A clock ticking towards midnight, then a far-off firework.
    night: () => {
      for (let i = 0; i < 6; i++) tick(i * 0.35, i % 2 ? 1600 : 2000, 0.07);
      bell(2.1, hz("F4"), 0.1, 1.2);
    },
    // "Auld Lang Syne", fireworks and party horns.
    podium: () => {
      melody(0, [["C5", 1], ["F5", 1.5], ["F5", 0.5], ["F5", 1], ["A5", 1], ["G5", 1.5], ["F5", 0.5], ["G5", 1], ["A5", 1], ["F5", 2]], 0.2, (t, f) => glock(t, f, 0.11, 0.6));
      [["F3", 0.2], ["C3", 1.2], ["F3", 2]].forEach(([n, t]) => triBass(t, hz(n), 0.5, 0.2));
      partyHorn(2.1, 0.04);
      burst(2.1, 0.25);
      burst(2.5, 0.2);
    },
  },
});

// Valentine's Day: a harp, soft strings, kisses and a heartbeat.
const valentine = makeSet({
  scale: ["Eb5", "F5", "G5", "Ab5", "Bb5", "C6", "D6"],
  lead: (at, f) => harp(at, f, 0.12),
  click: (at, f) => glock(at, f, 0.08, 0.35),
  chord: (at, freqs, len) => {
    freqs.forEach((f, i) => harp(at + i * 0.04, f, 0.1, len + 0.6));
    strings(at, freqs[0] / 2, len, 0.012);
  },
  hit: (at, vol = 0.35) => heartbeat(at, vol * 0.8),
  garnish: (at) => ["Bb6", "Eb7", "G7"].forEach((n, i) => glock(at + i * 0.05, hz(n), 0.04, 0.5)),
  sounds: {
    // Heartbreak: the strings sink and a heart cracks.
    wrong: () => {
      ["G5", "F5", "Eb5", "C5"].forEach((n, i) => harp(i * 0.25, hz(n), 0.11, 0.8));
      strings(0, hz("C4"), 1, 0.014);
      strings(0, hz("Eb4"), 1, 0.012);
      crack(1, 0.1);
    },
    squeak: () => kiss(0, 0.09),
    honk: () => {
      kiss(0, 0.1);
      kiss(0.18, 0.1);
    },
    // A harp sweep up.
    slideUp: () => ["Eb5", "G5", "Bb5", "D6", "Eb6", "G6"].forEach((n, i) => harp(i * 0.035, hz(n), 0.09, 0.6)),
    start: () => {
      ["Eb4", "G4", "Bb4", "Eb5", "G5", "Bb5", "Eb6"].forEach((n, i) => harp(i * 0.05, hz(n), 0.1, 1));
      strings(0.35, hz("Eb4"), 0.8, 0.015);
      strings(0.35, hz("G4"), 0.8, 0.012);
    },
    // Dice tumbling as little harp plucks.
    roll: () => ["Bb5", "G5", "Eb6", "C6", "Ab5", "F5", "D6"].forEach((n, i) => harp([0, 0.06, 0.1, 0.17, 0.22, 0.3, 0.4][i], hz(n), 0.09 - i * 0.006, 0.3)),
    coin: () => {
      glock(0, hz("Bb5"), 0.09, 0.4);
      glock(0.08, hz("Eb6"), 0.1, 0.9);
    },
    boom: () => {
      heartbeat(0, 0.5);
      heartbeat(0.6, 0.5);
    },
    dun: () => {
      heartbeat(0, 0.45);
      heartbeat(0.55, 0.5);
      strings(0.55, hz("C4"), 0.9, 0.018);
      strings(0.55, hz("Eb4"), 0.9, 0.015);
    },
    // A slow heartbeat and soft strings.
    night: () => {
      heartbeat(0, 0.3);
      heartbeat(0.9, 0.3);
      strings(0.2, hz("Ab3"), 1.4, 0.014);
      strings(0.2, hz("C4"), 1.4, 0.012);
      harp(1.4, hz("Eb5"), 0.08, 1);
    },
  },
});

// Carnival: a brass band, a samba whistle, cowbells and confetti.
const carnival = makeSet({
  scale: ["C5", "D5", "E5", "F5", "G5", "A5", "B5"],
  lead: (at, f, len = 0.1) => brass(at, f / 2, Math.max(0.06, len), 0.065),
  click: (at, f) => cowbell(at, Math.min(f / 2, 1100), 0.035),
  hit: (at, vol = 0.35) => {
    kick(at, vol);
    noise(at, { filter: "highpass", freq: 5000, vol: 0.05, len: 0.15 });
  },
  garnish: (at) => {
    whistle(at, 0.08, 0.035);
    shaker(at + 0.1, 0.05);
    shaker(at + 0.2, 0.05);
  },
  sounds: {
    // Three samba whistle blasts.
    siren: () => [0, 0.25, 0.5].forEach((t) => whistle(t, 0.15, 0.045)),
    honk: () => {
      kazoo(0, hz("G4"), 0.1);
      kazoo(0.16, hz("G4"), 0.14);
    },
    hmm: () => kazoo(0, hz("E4"), 0.3, 0.05),
    squeak: () => whistle(0, 0.06, 0.04),
    // A samba whistle, the drums kick in, and the band hits a chord.
    start: () => {
      whistle(0, 0.08);
      whistle(0.15, 0.08);
      whistle(0.3, 0.2);
      [0.6, 0.75, 0.9, 0.97, 1.05].forEach((t, i) => (i % 2 ? cowbell(t, 800) : kick(t, 0.3)));
      [hz("C4"), hz("E4"), hz("G4")].forEach((f) => brass(1.05, f, 0.5));
    },
    roll: () => [0, 0.08, 0.13, 0.21, 0.26, 0.34, 0.42].forEach((t, i) => (i % 3 ? shaker(t, 0.06) : cowbell(t, 700 + i * 40, 0.03))),
    coin: () => {
      cowbell(0, 900);
      cowbell(0.08, 1200);
    },
    sparkle: () => {
      partyHorn(0, 0.04);
      for (let i = 0; i < 10; i++) noise(0.05 + i * 0.03, { filter: "highpass", freq: 6000, vol: 0.04, len: 0.01 });
    },
    boom: () => {
      kick(0, 0.45);
      noise(0, { filter: "bandpass", freq: 1800, to: 6000, q: 0.8, vol: 0.2, len: 0.35 });
      for (let i = 0; i < 14; i++) noise(0.1 + i * 0.035, { filter: "highpass", freq: 6000, vol: 0.05, len: 0.01 });
    },
    // A street party winding down: distant drums and a lone kazoo.
    night: () => {
      [0, 0.4, 0.8, 1.2].forEach((t) => kick(t, 0.18));
      [0.2, 0.6, 1.0].forEach((t) => cowbell(t, 700, 0.015));
      kazoo(1.4, hz("C4"), 0.5, 0.03);
    },
    // The band plays it out, with a whistle on top.
    podium: () => {
      [["C4", 0], ["F4", 0.3], ["G4", 0.6], ["C5", 0.9]].forEach(([n, t], i) => {
        const f = hz(n);
        [f, f * 1.25, f * 1.5].forEach((x) => brass(t, x, i === 3 ? 0.8 : 0.2, 0.025));
        kick(t, 0.35);
        cowbell(t + 0.15, 800, 0.03);
      });
      whistle(0.9, 0.5);
    },
  },
});

// Ramadan: lantern chimes, the ney, the oud and a soft darbuka, in the Hijaz
// scale. (No call to prayer: it isn't a game sound.)
const HIJAZ = ["D5", "Eb5", "F#5", "G5", "A5", "Bb5", "C6"];
const ramadan = makeSet({
  scale: HIJAZ,
  lead: (at, f) => oud(at, f / 2, 0.11),
  click: (at, f) => glock(at, f, 0.08, 0.4),
  chord: (at, freqs, len) => {
    freqs.forEach((f, i) => glock(at + i * 0.05, f, 0.06, len + 0.5));
    ney(at, freqs[0] / 2, len, 0.08);
  },
  hit: (at, vol = 0.35) => doum(at, vol * 0.8),
  garnish: (at) => {
    glock(at, hz("A6"), 0.05, 0.6);
    glock(at + 0.09, hz("D7"), 0.05, 0.9);
  },
  sounds: {
    // The oud stepping down the Hijaz scale.
    wrong: () => {
      ["A4", "G4", "F#4", "Eb4", "D4"].forEach((n, i) => oud(i * 0.2, hz(n), 0.11, i === 4 ? 0.9 : 0.4));
      doum(0.8, 0.25);
    },
    // A slow ney phrase over the darbuka.
    start: () => {
      doum(0, 0.25);
      tek(0.25);
      tek(0.38);
      doum(0.5, 0.25);
      melody(0.1, [["D5", 1], ["Eb5", 0.5], ["F#5", 0.5], ["G5", 1], ["F#5", 0.5], ["Eb5", 0.5], ["D5", 2]], 0.18, (t, f, len) => ney(t, f, len, 0.1));
    },
    coin: () => {
      glock(0, hz("A5"), 0.08, 0.5);
      glock(0.09, hz("D6"), 0.09, 1);
    },
    roll: () => [0, 0.07, 0.12, 0.2, 0.25, 0.33].forEach((t, i) => (i % 3 === 0 ? doum(t, 0.2) : tek(t, 0.08))),
    // The iftar cannon.
    boom: () => {
      kick(0, 0.5);
      noise(0, { filter: "lowpass", freq: 1200, to: 60, vol: 0.4, len: 1 });
      noise(0.05, { filter: "lowpass", freq: 300, vol: 0.2, env: { a: 0.05, d: 0.6, s: 0.2, r: 0.8 } });
    },
    // Crickets in the warm night and a ney far away.
    night: () => {
      crickets(0, 2.2, 0.025);
      melody(0.4, [["A4", 1], ["Bb4", 0.5], ["A4", 0.5], ["G4", 1], ["F#4", 1.5]], 0.3, (t, f, len) => ney(t, f, len, 0.08));
    },
    // Qanun runs and a lantern chime.
    podium: () => {
      ["D4", "Eb4", "F#4", "G4", "A4", "Bb4", "C5", "D5"].forEach((n, i) => qanun(i * 0.08, hz(n)));
      [0, 0.32, 0.48, 0.64].forEach((t, i) => (i % 2 ? tek(t) : doum(t, 0.3)));
      [hz("D5"), hz("F#5"), hz("A5"), hz("D6")].forEach((f, i) => glock(0.7 + i * 0.06, f, 0.07, 1.4));
      ney(0.7, hz("D4"), 1, 0.08);
    },
  },
});

// Eid al-Fitr: a happy party, with the qanun, oud and quick darbuka in Ajam
// (a major scale).
const eidFitr = makeSet({
  scale: ["C5", "D5", "E5", "F5", "G5", "A5", "B5"],
  lead: (at, f) => qanun(at, f / 2, 0.09),
  click: (at, f) => {
    tek(at, 0.08);
    glock(at, f, 0.05, 0.25);
  },
  hit: (at, vol = 0.35) => doum(at, vol),
  garnish: (at) => {
    [0, 0.06, 0.12].forEach((t) => tek(at + t, 0.07));
    glock(at + 0.15, hz("G6"), 0.05, 0.6);
  },
  pad: (at, freqs, len) => freqs.forEach((f) => oud(at, f / 2, 0.06, len + 0.4)),
  sounds: {
    wrong: () => {
      ["G4", "F4", "E4", "D4", "C4"].forEach((n, i) => qanun(i * 0.18, hz(n) / 2, 0.08));
      doum(0.75, 0.25);
    },
    // A quick darbuka pattern, then an oud flourish.
    start: () => {
      [[0, "d"], [0.12, "t"], [0.24, "t"], [0.36, "d"], [0.48, "t"], [0.54, "t"], [0.6, "t"]].forEach(([t, k]) => (k === "d" ? doum(t, 0.3) : tek(t, 0.09)));
      ["C4", "E4", "G4", "C5", "E5"].forEach((n, i) => oud(0.72 + i * 0.07, hz(n), 0.05, 0.5));
      doum(1.05, 0.35);
    },
    roll: () => [0, 0.06, 0.1, 0.18, 0.22, 0.3, 0.36, 0.42].forEach((t, i) => (i % 4 === 0 ? doum(t, 0.22) : tek(t, 0.08))),
    coin: () => {
      glock(0, hz("E6"), 0.08, 0.4);
      glock(0.07, hz("G6"), 0.09, 0.9);
    },
    boom: () => {
      doum(0, 0.5);
      noise(0, { filter: "lowpass", freq: 900, to: 80, vol: 0.25, len: 0.6 });
      tek(0.12, 0.1);
    },
    // Families out late: a soft oud and crickets.
    night: () => {
      crickets(0, 2, 0.02);
      melody(0.3, [["G4", 1], ["A4", 0.5], ["G4", 0.5], ["E4", 1], ["D4", 0.5], ["C4", 1.5]], 0.25, (t, f) => oud(t, f, 0.05, 0.6));
    },
    // The whole band, faster and faster.
    podium: () => {
      [0, 0.15, 0.3, 0.38, 0.45, 0.6, 0.68, 0.75, 0.82, 0.9].forEach((t, i) => (i % 3 === 0 ? doum(t, 0.3) : tek(t, 0.09)));
      ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5"].forEach((n, i) => qanun(i * 0.07, hz(n), 0.04));
      [hz("C4"), hz("E4"), hz("G4"), hz("C5")].forEach((f) => oud(0.9, f, 0.05, 1.2));
      [hz("C6"), hz("E6"), hz("G6")].forEach((f, i) => glock(0.95 + i * 0.07, f, 0.07, 1));
    },
  },
});

// Eid al-Adha: the same band in Bayati, a scale with a quarter tone (E half-flat).
const eidAdha = makeSet({
  scale: ["D5", "Eq5", "F5", "G5", "A5", "Bb5", "C6"],
  lead: (at, f) => oud(at, f / 2, 0.11),
  click: (at, f) => glock(at, f, 0.07, 0.3),
  hit: (at, vol = 0.35) => doum(at, vol),
  garnish: (at) => {
    [0, 0.07].forEach((t) => tek(at + t, 0.07));
    glock(at + 0.12, hz("A6"), 0.05, 0.6);
  },
  sounds: {
    // Down the Bayati scale, through its quarter tone.
    wrong: () => {
      ["A4", "G4", "F4", "Eq4", "D4"].forEach((n, i) => oud(i * 0.2, hz(n), 0.11, i === 4 ? 0.9 : 0.4));
      doum(0.8, 0.25);
    },
    start: () => {
      [[0, "d"], [0.2, "t"], [0.3, "d"], [0.5, "t"], [0.6, "t"]].forEach(([t, k]) => (k === "d" ? doum(t, 0.3) : tek(t, 0.09)));
      melody(0.7, [["D4", 0.5], ["Eq4", 0.5], ["F4", 0.5], ["G4", 0.5], ["A4", 1.5]], 0.12, (t, f) => oud(t, f, 0.055, 0.6));
      doum(1.1, 0.35);
    },
    roll: () => [0, 0.08, 0.14, 0.22, 0.3, 0.36].forEach((t, i) => (i % 2 ? tek(t, 0.08) : doum(t, 0.2))),
    coin: () => {
      glock(0, hz("A5"), 0.08, 0.4);
      glock(0.08, hz("D6"), 0.09, 0.9);
    },
    boom: () => {
      doum(0, 0.5);
      doum(0.15, 0.35);
      noise(0, { filter: "lowpass", freq: 900, to: 80, vol: 0.25, len: 0.6 });
    },
    night: () => {
      crickets(0, 2, 0.02);
      melody(0.3, [["A4", 1], ["G4", 0.5], ["F4", 0.5], ["Eq4", 1], ["D4", 1.5]], 0.28, (t, f, len) => ney(t, f, len, 0.08));
    },
    podium: () => {
      [0, 0.2, 0.3, 0.5, 0.6, 0.7, 0.8].forEach((t, i) => (i % 2 ? tek(t, 0.09) : doum(t, 0.3)));
      ["D4", "Eq4", "F4", "G4", "A4", "Bb4", "C5", "D5"].forEach((n, i) => qanun(i * 0.08, hz(n), 0.04));
      [hz("D4"), hz("F4"), hz("A4"), hz("D5")].forEach((f) => oud(0.85, f, 0.05, 1.2));
      ney(0.85, hz("A4"), 1, 0.07);
    },
  },
});

// St Patrick's Day: a tin whistle jig, a bodhrán and a pot of gold.
const stpatricks = makeSet({
  scale: ["G5", "A5", "B5", "C6", "D6", "E6", "F#6"],
  lead: (at, f, len) => tinWhistle(at, f / 2, Math.min(0.15, len || 0.1)),
  click: (at, f) => harp(at, f / 2, 0.08, 0.4),
  chord: (at, freqs, len) => freqs.forEach((f, i) => harp(at + i * 0.03, f / 2, 0.1, len + 0.5)),
  hit: (at, vol = 0.35) => bodhran(at, vol * 0.9),
  garnish: (at) => ["D7", "G7"].forEach((n, i) => glock(at + i * 0.06, hz(n), 0.05, 0.5)),
  pad: (at, freqs, len) => strings(at, freqs[0] / 4, len, 0.012),
  sounds: {
    wrong: () => {
      ["D5", "C5", "B4", "G4"].forEach((n, i) => tinWhistle(i * 0.26, hz(n), i === 3 ? 0.5 : 0.18));
      bodhran(0.78, 0.3);
    },
    // A few bars of a jig.
    start: () => {
      melody(0, [["D5", 1], ["G5", 1], ["G5", 1], ["B5", 1], ["G5", 1], ["B5", 1], ["D6", 1], ["B5", 1], ["G5", 1], ["A5", 1], ["B5", 1], ["G5", 2]], 0.11, (t, f) => tinWhistle(t, f, 0.08));
      [0, 0.33, 0.66, 0.99].forEach((t) => bodhran(t, 0.25));
    },
    roll: () => [0, 0.06, 0.12, 0.2, 0.26, 0.32, 0.42].forEach((t, i) => bodhran(t, 0.18 - i * 0.01)),
    // A pot of gold: a run of glinting coins.
    coin: () => ["G6", "B6", "D7"].forEach((n, i) => glock(i * 0.06, hz(n), 0.07, i === 2 ? 1 : 0.3)),
    sparkle: () => ["D6", "G6", "B6", "D7", "G7"].forEach((n, i) => glock(i * 0.04, hz(n), 0.05, 0.4)),
    // Mist rolling over the hills and a harp.
    night: () => {
      noise(0, { filter: "bandpass", freq: 600, to: 900, q: 0.7, vol: 0.09, env: { a: 0.6, d: 0.5, s: 0.3, r: 0.8 } });
      ["G4", "B4", "D5", "G5", "B5", "D6"].forEach((n, i) => harp(0.6 + i * 0.08, hz(n), 0.08, 1.2));
    },
    podium: () => {
      melody(0, [["G5", 1], ["A5", 1], ["B5", 1], ["C6", 1], ["D6", 1], ["E6", 1], ["D6", 2], ["B5", 1], ["G5", 3]], 0.11, (t, f) => tinWhistle(t, f, 0.08));
      [0, 0.33, 0.66, 0.99, 1.1].forEach((t) => bodhran(t, 0.25));
      ["G3", "B3", "D4", "G4"].forEach((n, i) => harp(1.1 + i * 0.04, hz(n), 0.1, 1.3));
    },
  },
});

// Easter: birdsong, a marimba and cracking eggs.
const easter = makeSet({
  scale: ["F5", "G5", "A5", "Bb5", "C6", "D6", "E6"],
  lead: (at, f) => marimba(at, f / 2, 0.17),
  click: (at, f) => marimba(at, f, 0.1, 0.2),
  hit: (at, vol = 0.35) => kick(at, vol * 0.6),
  garnish: (at) => birdChirp(at, 3900, 0.04),
  sounds: {
    // An egg drops and splats.
    wrong: () => {
      tone(0, 900, { type: "sine", vol: 0.1, to: 250, slide: 0.35, env: { d: 0.35, s: 0.0001, r: 0.03 } });
      noise(0.4, { filter: "lowpass", freq: 1800, to: 300, vol: 0.25, len: 0.3 });
      crack(0.4, 0.12);
      ["C5", "A4", "F4"].forEach((n, i) => marimba(0.7 + i * 0.18, hz(n), 0.14, 0.5));
    },
    // Birds raising the alarm.
    siren: () => [0, 0.2, 0.4, 0.6].forEach((t, i) => birdChirp(t, i % 2 ? 4400 : 3400, 0.05)),
    squeak: () => birdChirp(0, 3800, 0.04),
    // A bunny hop.
    boing: () => {
      tone(0, 300, { type: "sine", vol: 0.14, to: 700, slide: 0.1, env: { d: 0.12, s: 0.0001, r: 0.03 } });
      tone(0.16, 350, { type: "sine", vol: 0.12, to: 800, slide: 0.1, env: { d: 0.12, s: 0.0001, r: 0.03 } });
    },
    start: () => {
      birdChirp(0, 3600);
      birdChirp(0.2, 4200);
      ["F4", "A4", "C5", "F5"].forEach((n, i) => marimba(0.45 + i * 0.08, hz(n), 0.17));
      [hz("F4"), hz("A4"), hz("C5")].forEach((f) => marimba(0.8, f, 0.12, 0.8));
    },
    // Eggs cracking.
    roll: () =>
      [0, 0.1, 0.16, 0.3, 0.38].forEach((t, i) => {
        noise(t, { filter: "bandpass", freq: 2500 + i * 300, q: 3, vol: 0.12, len: 0.012 });
        noise(t + 0.01, { filter: "highpass", freq: 4000, vol: 0.05, len: 0.03 });
      }),
    coin: () => {
      marimba(0, hz("C6"), 0.12, 0.3);
      marimba(0.08, hz("F6"), 0.12, 0.6);
    },
    sparkle: () => {
      birdChirp(0, 4000, 0.04);
      ["C6", "D6", "F6", "A6"].forEach((n, i) => marimba(0.1 + i * 0.04, hz(n), 0.08, 0.3));
    },
    // A spring evening: birds settling down.
    night: () => {
      [0, 0.5, 0.8, 1.4].forEach((t, i) => birdChirp(t, 3200 + i * 300, 0.04));
      crickets(0.6, 1.6, 0.018);
    },
    podium: () => {
      ["F4", "G4", "A4", "C5", "D5", "F5"].forEach((n, i) => marimba(i * 0.08, hz(n), 0.16));
      [hz("F4"), hz("A4"), hz("C5"), hz("F5")].forEach((f) => marimba(0.55, f, 0.12, 1));
      kick(0.55, 0.25);
      birdChirp(0.6, 3800);
      birdChirp(0.85, 4400);
    },
  },
});

// April Fools': whoopee cushions, slide whistles and clown horns.
const aprilfools = makeSet({
  scale: ["C5", "D5", "E5", "F5", "G5", "A5", "B5"],
  lead: (at, f, len = 0.08) => kazoo(at, f / 2, Math.max(0.05, len), 0.1),
  click: (at, f) => tone(at, f / 2, { type: "sine", vol: 0.12, to: f, slide: 0.04, env: { d: 0.05, s: 0.0001, r: 0.02 } }),
  hit: (at, vol = 0.35) => bonk(at, vol * 0.7),
  garnish: (at) => slideWhistle(at, 800, 1500, 0.12, 0.08),
  sounds: {
    siren: () => {
      clownHorn(0);
      clownHorn(0.25);
      clownHorn(0.5);
    },
    boing: () => slideWhistle(0, 500, 1400, 0.2),
    squeak: () => clownHorn(0, 0.04),
    honk: () => {
      clownHorn(0);
      clownHorn(0.2);
    },
    slideUp: () => slideWhistle(0, 400, 1600, 0.25),
    slideDown: () => slideWhistle(0, 1600, 400, 0.3),
    start: () => {
      slideWhistle(0, 400, 1600, 0.35);
      bonk(0.5, 0.3);
      clownHorn(0.75);
    },
    wrong: () => whoopee(0),
    skip: () => slideWhistle(0, 1400, 500, 0.2),
    roll: () => [0, 0.1, 0.18, 0.3, 0.4].forEach((t, i) => tone(t, 600 + i * 150, { type: "sine", vol: 0.12, to: 300, slide: 0.05, env: { d: 0.06, s: 0.0001, r: 0.02 } })),
    boom: () => bonk(0, 0.45),
    dun: () => {
      [0, 0.28].forEach((t) => bonk(t, 0.3));
      whoopee(0.56, 0.06);
    },
    // Snoring.
    night: () =>
      [0, 1.1].forEach((t) => {
        tone(t, 90, { type: "sawtooth", vol: 0.05, to: 70, vib: [30, 20], filter: { freq: 600 }, env: { a: 0.3, d: 0.1, s: 0.8, hold: 0.4, r: 0.2 } });
        slideWhistle(t + 0.75, 900, 1300, 0.15, 0.06);
      }),
    podium: () => {
      slideWhistle(0, 400, 1600, 0.3);
      [hz("C4"), hz("E4"), hz("G4")].forEach((f) => kazoo(0.4, f, 0.6, 0.035));
      clownHorn(0.4);
      clownHorn(0.65);
      bonk(1.1, 0.3);
    },
  },
});

// Summer: steel pans, a ukulele, waves and seagulls.
const summer = makeSet({
  scale: ["F5", "G5", "A5", "Bb5", "C6", "D6", "E6"],
  lead: (at, f) => steelpan(at, f / 2, 0.12),
  click: (at, f) => uke(at, f / 2, 0.09, 0.25),
  chord: (at, freqs, len) => freqs.forEach((f, i) => uke(at + i * 0.025, f / 2, 0.08, len + 0.3)),
  hit: (at, vol = 0.35) => {
    kick(at, vol * 0.8);
    shaker(at + 0.12, 0.04);
  },
  garnish: (at) => {
    shaker(at, 0.05);
    shaker(at + 0.12, 0.05);
    steelpan(at + 0.12, hz("F6"), 0.05, 0.4);
  },
  sounds: {
    // Steel pans sinking, with a bubble.
    wrong: () => {
      ["A5", "G5", "F5", "C5"].forEach((n, i) => steelpan(i * 0.25, hz(n), 0.11, 0.6));
      sink(0.9, 0.1);
    },
    // A lifeguard's whistle.
    siren: () => [0, 0.45].forEach((t) => tone(t, 2900, { type: "sine", vol: 0.07, vib: [3, 30], env: { a: 0.01, d: 0.02, s: 0.9, hold: 0.32, r: 0.03 } })),
    pop: () => tone(0, 400, { type: "sine", vol: 0.15, to: 1100, slide: 0.05, env: { d: 0.06, s: 0.0001, r: 0.02 } }),
    start: () => {
      ["F4", "A4", "C5", "F5", "A5"].forEach((n, i) => steelpan(i * 0.08, hz(n), 0.12));
      [0, 0.2, 0.3, 0.5].forEach((t) => shaker(t, 0.05));
      [hz("F4"), hz("A4"), hz("C5")].forEach((f, i) => uke(0.45 + i * 0.02, f, 0.09, 0.7));
      kick(0.45, 0.3);
    },
    roll: () => [0, 0.08, 0.16, 0.24, 0.32].forEach((t) => shaker(t, 0.07)),
    coin: () => {
      steelpan(0, hz("C6"), 0.1, 0.4);
      steelpan(0.08, hz("F6"), 0.1, 0.8);
    },
    // A splash, and bubbles.
    boom: () => {
      noise(0, { filter: "bandpass", freq: 3000, to: 400, q: 0.8, vol: 0.3, len: 0.5 });
      kick(0, 0.3);
      [0.25, 0.32, 0.4, 0.5].forEach((t, i) => tone(t, 400 + i * 120, { type: "sine", vol: 0.1, to: 1000 + i * 200, slide: 0.04, env: { d: 0.05, s: 0.0001, r: 0.02 } }));
    },
    // Waves on the beach and a seagull.
    night: () => {
      waves(0, 0.12, 1.6);
      waves(1.3, 0.1, 1.6);
      gull(0.6, 0.04);
    },
    podium: () => {
      melody(0, [["C5", 0.5], ["F5", 0.5], ["A5", 0.5], ["G5", 1], ["F5", 0.5], ["A5", 0.5], ["C6", 1.5], ["A5", 0.5], ["F5", 2]], 0.16, (t, f) => steelpan(t, f, 0.12));
      [0, 0.32, 0.64, 0.96, 1.28].forEach((t) => {
        kick(t, 0.25);
        shaker(t + 0.16, 0.05);
      });
      gull(1.5, 0.035);
    },
  },
});

// The New Year countdown (lib/newYear.js): a tick for each second (higher
// for the last three), then the party at midnight: fireworks, party horns and
// "Auld Lang Syne" on bells and brass.
export const NEW_YEAR_SOUNDS = {
  tick: (n) => {
    tick(0, n <= 3 ? 2600 : 1900, n <= 3 ? 0.12 : 0.08);
    if (n <= 3) glock(0, hz(["C6", "E6", "G6"][3 - n]), 0.07, 0.4);
  },
  midnight: () => {
    burst(0, 0.4);
    partyHorn(0, 0.05);
    partyHorn(0.05, 0.04);
    fizz(0.1, 1.2, 0.05);
    const tune = [["C5", 1], ["F5", 1.5], ["F5", 0.5], ["F5", 1], ["A5", 1], ["G5", 1.5], ["F5", 0.5], ["G5", 1], ["A5", 1], ["F5", 1.5], ["F5", 0.5], ["A5", 1], ["C6", 1], ["D6", 3]];
    melody(0.6, tune, 0.26, (t, f) => glock(t, f, 0.11, 0.7));
    [["F3", 0.6, 1], ["Bb3", 1.65, 1], ["C4", 2.7, 1], ["F3", 3.75, 1], ["Bb3", 4.55, 1.6]].forEach(([n, t, len]) => {
      const f = hz(n);
      [f, f * 1.25, f * 1.5].forEach((x) => brass(t, x, len, 0.02));
      kick(t, 0.25);
    });
    [1.3, 2.4, 3.3, 4.6, 5.4].forEach((t, i) => {
      rocket(t - 0.5, 0.03);
      burst(t, 0.2 + (i % 2) * 0.08);
    });
  },
};

export const EVENT_SOUNDS = { christmas, newyear, valentine, carnival, ramadan, eidFitr, eidAdha, stpatricks, easter, aprilfools, summer };
addSoundSets(EVENT_SOUNDS);
